import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { STATUS_TYPES } from "./meetingDetailsConstants";

const STATUS_META = {
  Scheduled: { short: "Sched", hue: 199 },
  Created: { short: "New", hue: 152 },
  DatesOffered: { short: "Offer", hue: 45 },
  AdminCare: { short: "Care", hue: 277 },
  Done: { short: "Done", hue: 130 },
  NoShow: { short: "N/S", hue: 12 },
  Canceled: { short: "Canx", hue: 340 },
};

/**
 * Vertical status rail — left column navigation for consultations (sidebar layout).
 */
export default function MeetingDetailsStatusSidebar({
  status,
  selectedType,
  onSelect,
}) {
  const byType = Object.fromEntries(status.map((s) => [s.type, s]));

  return (
    <Box
      component="nav"
      aria-label="Meeting status"
      sx={{
        width: { xs: 200, sm: 220 },
        flexShrink: 0,
        borderRadius: 3,
        py: 2,
        px: 1.25,
        background:
          "linear-gradient(165deg, rgba(15,23,42,0.97) 0%, rgba(30,41,59,0.94) 50%, rgba(15,23,42,0.98) 100%)",
        border: "1px solid rgba(148,163,184,0.12)",
        boxShadow:
          "0 4px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 80% 50% at 0% 0%, rgba(56,189,248,0.08), transparent 55%)",
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          px: 1.5,
          mb: 1.5,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "rgba(148,163,184,0.85)",
          fontWeight: 600,
          fontSize: "0.65rem",
        }}
      >
        Status
      </Typography>
      <Box
        component="ul"
        sx={{
          listStyle: "none",
          m: 0,
          p: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          position: "relative",
          zIndex: 1,
        }}
      >
        {STATUS_TYPES.map((type) => {
          const s = byType[type];
          const count = s?.totalCount ?? 0;
          const active = selectedType === type;
          const meta = STATUS_META[type] || { short: type.slice(0, 4), hue: 200 };

          return (
            <Box key={type} component="li" sx={{ m: 0, p: 0 }}>
              <Box
                component="button"
                type="button"
                onClick={() => onSelect(type)}
                aria-pressed={active}
                sx={{
                  width: "100%",
                  textAlign: "left",
                  cursor: "pointer",
                  border: "none",
                  borderRadius: 2,
                  py: 1.25,
                  px: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  position: "relative",
                  overflow: "hidden",
                  color: active ? "#f8fafc" : "rgba(203,213,225,0.92)",
                  background: active
                    ? `linear-gradient(105deg, hsla(${meta.hue},70%,42%,0.45) 0%, hsla(${meta.hue},65%,38%,0.15) 100%)`
                    : "transparent",
                  boxShadow: active
                    ? `0 0 0 1px hsla(${meta.hue},75%,55%,0.35), 0 8px 20px -6px hsla(${meta.hue},80%,40%,0.35)`
                    : "none",
                  transition:
                    "background 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1), color 0.25s ease, transform 0.2s ease",
                  transform: active ? "scale(1.02)" : "scale(1)",
                  "&::before": active
                    ? {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "12%",
                        bottom: "12%",
                        width: 3,
                        borderRadius: "0 4px 4px 0",
                        background: `linear-gradient(180deg, hsl(${meta.hue}, 90%, 62%), hsl(${meta.hue}, 85%, 48%))`,
                        boxShadow: `0 0 12px hsla(${meta.hue}, 100%, 60%, 0.65)`,
                      }
                    : {},
                  "&:hover": {
                    background: active
                      ? undefined
                      : "rgba(148,163,184,0.08)",
                    transform: active ? "scale(1.02)" : "scale(1.01)",
                  },
                  "&:focus-visible": {
                    outline: "2px solid rgba(56,189,248,0.7)",
                    outlineOffset: 2,
                  },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: active ? 700 : 500,
                      fontSize: "0.82rem",
                      lineHeight: 1.35,
                      letterSpacing: "0.02em",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {type}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: "none", sm: "block" },
                      color: "rgba(148,163,184,0.75)",
                      fontSize: "0.62rem",
                      mt: 0.25,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {meta.short}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flexShrink: 0,
                    minWidth: 28,
                    height: 24,
                    px: 0.75,
                    borderRadius: 999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    color: active ? "#0f172a" : "rgba(226,232,240,0.95)",
                    background: active
                      ? `linear-gradient(135deg, hsl(${meta.hue}, 85%, 78%), hsl(${meta.hue}, 75%, 65%))`
                      : "rgba(51,65,85,0.65)",
                    border: active
                      ? `1px solid hsla(${meta.hue}, 70%, 70%, 0.5)`
                      : "1px solid rgba(100,116,139,0.35)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {count > 99 ? "99+" : count}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
